// The funnel's one way to report itself (ENG-3796 × ENG-4305).
//
// WHY THIS REPLACED A SECOND FRAMEWORK. This funnel originally captured through
// `usePostHog().capture` at eleven screen call sites. `main` had meanwhile
// shipped `analytics/domains/onboarding-lifecycle.ts` for this exact wizard,
// and its header says what that second path costs: capturing outside `track()`
// "skipped event-name validation, the context envelope and the provider
// fan-out". The two also collided outright — both registered
// `onboarding_step_viewed`, with different required properties, so whichever
// row won the canonical map the other emitter violated it.
//
// So the funnel now emits through the shared tracker. Every screen keeps its
// own reason to report; none of them owns an event name any more.
//
// The stage/step vocabulary is ENG-4305's (`ONBOARDING_STAGE` /
// `ONBOARDING_STEP`), which already enumerates every screen this funnel has —
// it was written for this wizard. Mapping our screen names onto it lives HERE,
// once, rather than as a literal at each call site.
//
// Per rules: investigate-before-adding, code-comments-required,
// reference-rules-in-comments, ai-code-requires-tests (ai.knowledge/global).

import { useMemo } from 'react';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { createOnboardingLifecycleTracker } from '#~/analytics/domains/onboarding-lifecycle.js';
import {
  EVENT_CATEGORIES,
  ONBOARDING_PROPERTIES,
  ONBOARDING_STAGE,
  ONBOARDING_STEP
} from '#~/analytics/constants/index.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import type { OnboardingContext } from '#~/analytics/domains/onboarding-lifecycle.js';
import type { EventName } from '#~/analytics/constants/event-names.js';

/** The funnel's own screen names, as the gate's FunnelStep union spells them. */
export type FunnelScreenName =
  | 'welcome'
  | 'bridge-concept'
  | 'detecting'
  | 'bridge-failure'
  | 'choose-method'
  | 'discovery'
  | 'manual-entry'
  | 'camera-model'
  | 'connection-test'
  | 'magic-moment'
  | 'complete';

/**
 * Where each screen sits in the shared taxonomy.
 *
 * Two screens deliberately share a step. `bridge-failure` is the failure face
 * of `bridge_detect`, not a step of its own — the user did not advance, the
 * same step went wrong, and giving it a separate step would split one funnel
 * position into two. `camera-model` reports as `camera_form` for the same
 * reason: identifying the camera is part of describing it, and ENG-4305's step
 * list has no separate entry because the step is "the user tells us about this
 * camera".
 */
const SCREEN_CONTEXT: Readonly<Record<FunnelScreenName, OnboardingContext>> = {
  'welcome': { stage: ONBOARDING_STAGE.WELCOME, step: ONBOARDING_STEP.WELCOME },
  'bridge-concept': { stage: ONBOARDING_STAGE.LINK_BRIDGE, step: ONBOARDING_STEP.BRIDGE_CONCEPT },
  'detecting': { stage: ONBOARDING_STAGE.LINK_BRIDGE, step: ONBOARDING_STEP.BRIDGE_DETECT },
  'bridge-failure': { stage: ONBOARDING_STAGE.LINK_BRIDGE, step: ONBOARDING_STEP.BRIDGE_DETECT },
  'choose-method': { stage: ONBOARDING_STAGE.DISCOVER, step: ONBOARDING_STEP.CHOOSE_METHOD },
  'discovery': { stage: ONBOARDING_STAGE.DISCOVER, step: ONBOARDING_STEP.DISCOVERY },
  'manual-entry': { stage: ONBOARDING_STAGE.ADD_CAMERA, step: ONBOARDING_STEP.CAMERA_FORM },
  'camera-model': { stage: ONBOARDING_STAGE.ADD_CAMERA, step: ONBOARDING_STEP.CAMERA_FORM },
  'connection-test': { stage: ONBOARDING_STAGE.ADD_CAMERA, step: ONBOARDING_STEP.CONNECTION_TEST },
  'magic-moment': { stage: ONBOARDING_STAGE.FIRST_STREAM, step: ONBOARDING_STEP.MAGIC_MOMENT },
  'complete': { stage: ONBOARDING_STAGE.COMPLETE, step: ONBOARDING_STEP.COMPLETE }
};

/** The shared taxonomy position for one of this funnel's screens. */
export const onboardingContextFor = (screen: FunnelScreenName): OnboardingContext =>
  SCREEN_CONTEXT[screen];

/**
 * The lifecycle tracker, bound to this render's `track`.
 *
 * Memoised on `track` exactly as the permissions wizard binds its own
 * (routes/electron/permissions/permissions.tsx), so a re-render does not build
 * a new tracker and invalidate every effect that depends on it.
 */
export const useOnboardingTracker = (): ReturnType<typeof createOnboardingLifecycleTracker> => {
  const { track } = useAnalytics();

  return useMemo(() => createOnboardingLifecycleTracker(track), [track]);
};

/**
 * Emit one of this funnel's OWN events — the four with no counterpart in
 * ENG-4305's vocabulary: `onboarding_discovery_result_count`,
 * `onboarding_address_resolved`, `onboarding_first_stream` and
 * `onboarding_first_ptz_action`.
 *
 * Still through `track()`, not `usePostHog().capture`: owning a name is not a
 * reason to skip event-name validation, the context envelope and the provider
 * fan-out. The stage/step pair is stamped here so these rows sit in the same
 * funnel as everything the lifecycle tracker sends.
 */
export const useOnboardingEvent = (): ((
  eventName: EventName,
  screen: FunnelScreenName,
  properties?: EventProperties
) => void) => {
  const { track } = useAnalytics();

  return useMemo(
    () =>
      (eventName: EventName, screen: FunnelScreenName, properties: EventProperties = {}): void => {
        const context = onboardingContextFor(screen);
        track(eventName, EVENT_CATEGORIES.ONBOARDING, {
          ...properties,
          [ONBOARDING_PROPERTIES.STAGE]: context.stage,
          [ONBOARDING_PROPERTIES.STEP]: context.step
        });
      },
    [track]
  );
};
